<script setup lang="ts">
import { ref, computed, watchEffect, watch } from "vue";
import { useSearchStore } from "@/stores/search";
import SearchBarInput from "@/components/SearchBar/SearchBarInput.vue";
import SearchModal from "@/components/SearchModal/SearchModal.vue";
import { searchBarItems } from "@/globalConsts/searchBarItems";
import { storeToRefs } from "pinia";
import { t } from "@/services/i18n";

const store = useSearchStore();
const { quickSearchFromStore, resetSeaContentAndTeaser } = store;
const activeSearchBar = computed(() => {
    return store.isSearchBarActive;
});
const currentStep = ref(-1);
const { currentSearchItems } = storeToRefs(store);
const { isPalm } = storeToRefs(store);
const quickSearchWrapper = ref<HTMLDivElement>();

async function search() {
    resetSeaContentAndTeaser();
    await quickSearchFromStore();
}

function next() {
    if (currentStep.value === searchBarItems.length - 1) {
        store.openSearchBar(false);
    } else {
        currentStep.value++;
    }
}

async function openModal(index = 0) {
    await store.openSearchBar(true);
    currentStep.value = index;
}

function openSearch() {
    if (activeSearchBar.value) {
        store.openSearchBar(false);
    } else {
        openModal();
    }
}

function isLastItem(index: number) {
    return searchBarItems.length - 1 === index;
}

function isOpenStep(index: number) {
    return currentStep.value === index && activeSearchBar.value;
}

function closeSearchBar() {
    store.openSearchBar(false);
}

watchEffect(() => {
    if (!activeSearchBar.value) {
        currentStep.value = -1;
    }
});

watch(
    () => currentStep.value,
    (newVal: number, oldVal: number) => {
        if (oldVal === 0) {
            search();
        }
    }
);
</script>
<template>
    <div class="[ c-search-bar ]" ref="quickSearchWrapper">
        <div
            class="[ c-search-bar__content ]"
            :class="{ 'is-active': activeSearchBar }"
            @click.stop
        >
            <div
                v-for="(searchBarItem, index) in searchBarItems"
                :key="searchBarItem.id"
                @click="openModal(index)"
                class="[ c-search-bar__item-wrapper ]"
            >
                <search-bar-input
                    v-if="searchBarItem.id === 'searchTerm'"
                    :id="searchBarItem.id"
                    :label="searchBarItem.title"
                    :placeholder="searchBarItem.defaultValue"
                    :value="currentSearchItems[searchBarItem.id]"
                    :input-readonly="searchBarItem.inputReadonly"
                    :is-open="isOpenStep(index) && !isPalm"
                    :key="'sbi-' + currentSearchItems[searchBarItem.id]"
                    :has-search-button="isLastItem(index)"
                    @next="next"
                />
                <search-bar-input
                    v-else
                    :id="searchBarItem.id"
                    :label="searchBarItem.title"
                    :placeholder="searchBarItem.defaultValue"
                    :value="currentSearchItems[searchBarItem.id]"
                    :input-readonly="searchBarItem.inputReadonly"
                    :is-open="isOpenStep(index) && !isPalm"
                    :key="'sbi-' + currentSearchItems[searchBarItem.id]"
                    :has-search-button="isLastItem(index)"
                    @next="next"
                    @on-submit="closeSearchBar"
                />
            </div>
            <button
                class="[ c-search-bar__button ]"
                @click="openSearch"
                type="submit"
                aria-label="Search"
            >
                <span v-if="activeSearchBar">{{ t("search") }}</span>
                <i class="[ c-icon icon-search c-icon--large ]"></i>
            </button>
            <search-modal
                :values="currentSearchItems"
                :open-step="currentStep"
                :is-open="activeSearchBar"
                :key="currentStep"
                @next-step="currentStep++"
                @set-step="(value: number) => (currentStep = value)"
                @search="closeSearchBar"
            />
        </div>
    </div>
</template>
